import React, { useState, useEffect, useCallback } from 'react';
import { staffApi, leaveTypesApi } from '../../services/api';
import toast from 'react-hot-toast';
import {
  HiOutlineUserGroup,
  HiOutlineCalendar,
  HiOutlineCurrencyRupee,
  HiOutlineBriefcase,
  HiOutlineClipboardList,
  HiPlus,
  HiPencil,
  HiKey,
  HiX
} from 'react-icons/hi';
import Attendance from './Attendance';
import Payroll from './Payroll';
import JobInquiries from './JobInquiries';
import StaffForm from '../../components/Forms/StaffForm';
import LeaveBalanceSummary from '../../components/Leave/LeaveBalanceSummary';
import { useAuth } from '../../context/AuthContext';
import { useClinic } from '../../context/ClinicContext';
import { usePermissions } from '../../hooks/usePermissions';
import { userAvatarSrc, resolveProfileImageSrc } from '../../utils/avatarUrl';
import { formatDateDMY } from '../../utils/dateFormat';

const Staff = () => {
  const { user } = useAuth();
  const { clinics } = useClinic();
  const isAdmin = user?.role === 'admin' || user?.role === 'clinic_admin';
  const { canRead } = usePermissions();
  const [staff, setStaff] = useState([]);
  const [activeTab, setActiveTab] = useState(() => {
    try {
      const tab = sessionStorage.getItem('staffActiveTab');
      if (tab === 'attendance' || tab === 'payroll' || tab === 'leave-requests' || tab === 'job-inquiries') {
        sessionStorage.removeItem('staffActiveTab');
        return tab;
      }
    } catch {
      /* ignore */
    }
    return 'list';
  });
  const [loading, setLoading] = useState(true);
  const [showStaffForm, setShowStaffForm] = useState(false);
  const [selectedStaff, setSelectedStaff] = useState(null);
  const [passwordModal, setPasswordModal] = useState({ open: false, staff: null });
  const [newPassword, setNewPassword] = useState('');
  const [savingPassword, setSavingPassword] = useState(false);
  const [leaveRequests, setLeaveRequests] = useState([]);
  const [leaveLoading, setLeaveLoading] = useState(false);
  const [leaveActionId, setLeaveActionId] = useState(null);
  const [pendingLeaveCount, setPendingLeaveCount] = useState(0);
  const [editingLeave, setEditingLeave] = useState(null);
  const [leaveTypes, setLeaveTypes] = useState([]);
  const [leaveEditForm, setLeaveEditForm] = useState({
    start_date: '',
    end_date: '',
    leave_type: '',
    reason: '',
    is_half_day: false,
  });

  const refreshPendingLeaveCount = useCallback(async () => {
    if (!user || !canRead('staff')) {
      setPendingLeaveCount(0);
      return;
    }
    try {
      const res = await staffApi.getLeaveRequestsPendingCount();
      setPendingLeaveCount(res.data.data?.pending_count ?? 0);
    } catch {
      setPendingLeaveCount(0);
    }
  }, [user, canRead]);

  useEffect(() => {
    refreshPendingLeaveCount();
    const t = setInterval(refreshPendingLeaveCount, 60000);
    const onChange = () => refreshPendingLeaveCount();
    window.addEventListener('pendingLeaveRequestsChanged', onChange);
    return () => {
      clearInterval(t);
      window.removeEventListener('pendingLeaveRequestsChanged', onChange);
    };
  }, [refreshPendingLeaveCount]);

  useEffect(() => {
    if (activeTab === 'list') {
      fetchStaff();
    }
  }, [activeTab]);

  useEffect(() => {
    if (activeTab !== 'leave-requests') return;
    const load = async () => {
      setLeaveLoading(true);
      try {
        const [reqRes, ltRes] = await Promise.all([
          staffApi.getLeaveRequests(),
          leaveTypesApi.getActive().catch(() => ({ data: { data: [] } })),
        ]);
        setLeaveRequests(reqRes.data.data || []);
        setLeaveTypes(ltRes.data?.data || []);
      } catch {
        toast.error('Failed to load leave requests');
      } finally {
        setLeaveLoading(false);
      }
    };
    load();
  }, [activeTab]);

  useEffect(() => {
    if (activeTab === 'leave-requests') {
      refreshPendingLeaveCount();
    }
  }, [activeTab, refreshPendingLeaveCount]);

  const fetchStaff = async () => {
    setLoading(true);
    try {
      const response = await staffApi.getAll();
      setStaff(response.data.data);
    } catch (error) {
      toast.error('Failed to fetch staff');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = async (staffMember) => {
    try {
      const response = await staffApi.getById(staffMember.id);
      setSelectedStaff(response.data.data);
      setShowStaffForm(true);
    } catch (error) {
      toast.error('Failed to load staff details');
    }
  };

  const handleAdd = () => {
    setSelectedStaff(null);
    setShowStaffForm(true);
  };

  const handleFormSuccess = (payload) => {
    fetchStaff();
    if (payload?.staff) {
      setSelectedStaff(payload.staff);
    }
  };


  const openResetPassword = (member) => {
    setPasswordModal({ open: true, staff: member });
    setNewPassword('');
  };

  const closeResetPassword = () => {
    setPasswordModal({ open: false, staff: null });
    setNewPassword('');
    setSavingPassword(false);
  };

  const leaveRowAvatar = (row) =>
    resolveProfileImageSrc(row.staff_profile_image_url, row.staff_profile_image);

  const handleLeaveStatus = async (id, status) => {
    setLeaveActionId(id);
    try {
      const res = await staffApi.updateLeaveRequestStatus(id, { status });
      toast.success(res.data?.message || (status === 'approved' ? 'Request approved' : 'Request rejected'));
      if (status === 'approved' || status === 'rejected') {
        window.dispatchEvent(new CustomEvent('staffAttendanceChanged'));
      }
      const listRes = await staffApi.getLeaveRequests();
      setLeaveRequests(listRes.data.data || []);
      await refreshPendingLeaveCount();
    } catch (e) {
      toast.error(e.response?.data?.message || 'Failed to update');
    } finally {
      setLeaveActionId(null);
    }
  };

  const isMonthlyLedgerType = (lt) =>
    lt.uses_monthly_ledger === 1 || lt.uses_monthly_ledger === true;

  const isHalfDayCompanionType = (lt) =>
    (lt.allows_half_day === 1 || lt.allows_half_day === true) &&
    !isMonthlyLedgerType(lt);

  const monthlyLedgerType = leaveTypes.find(isMonthlyLedgerType);
  const halfDayCompanionType = leaveTypes.find(isHalfDayCompanionType);

  const requestLeaveTypes = React.useMemo(() => {
    if (monthlyLedgerType && halfDayCompanionType) {
      return leaveTypes.filter(
        (lt) => lt.name === monthlyLedgerType.name || lt.name === halfDayCompanionType.name
      );
    }
    return leaveTypes;
  }, [leaveTypes, monthlyLedgerType, halfDayCompanionType]);

  const isHalfDayLeaveSelected = leaveEditForm.is_half_day;

  const handleLeaveEditTypeChange = (leaveTypeName) => {
    const nextType = leaveTypes.find((lt) => lt.name === leaveTypeName);
    const halfDayType = nextType && isHalfDayCompanionType(nextType);
    setLeaveEditForm((f) => ({
      ...f,
      leave_type: leaveTypeName,
      is_half_day: Boolean(halfDayType),
      end_date: halfDayType ? f.start_date : f.end_date,
    }));
  };

  const openLeaveEdit = (row) => {
    setEditingLeave(row);
    setLeaveEditForm({
      start_date: row.start_date || '',
      end_date: row.end_date || '',
      leave_type: row.leave_type || '',
      reason: row.reason || '',
      is_half_day: row.is_half_day === 1 || row.is_half_day === true,
    });
  };

  const closeLeaveEdit = () => {
    setEditingLeave(null);
    setLeaveEditForm({
      start_date: '',
      end_date: '',
      leave_type: '',
      reason: '',
      is_half_day: false,
    });
  };

  const handleLeaveEditSubmit = async (e) => {
    e.preventDefault();
    if (!editingLeave) return;

    if (!leaveEditForm.start_date || !leaveEditForm.end_date || !leaveEditForm.reason.trim()) {
      toast.error('Please fill start date, end date and reason');
      return;
    }
    if (monthlyLedgerType && !leaveEditForm.leave_type) {
      toast.error('Please select a leave type');
      return;
    }

    setLeaveActionId(editingLeave.id);
    try {
      await staffApi.updateLeaveRequest(editingLeave.id, {
        start_date: String(leaveEditForm.start_date).slice(0, 10),
        end_date: String(leaveEditForm.end_date).slice(0, 10),
        leave_type: leaveEditForm.leave_type?.trim() || undefined,
        reason: leaveEditForm.reason.trim(),
        is_half_day: leaveEditForm.is_half_day || undefined,
      });
      toast.success('Leave request updated');
      const res = await staffApi.getLeaveRequests();
      setLeaveRequests(res.data.data || []);
      closeLeaveEdit();
      await refreshPendingLeaveCount();
    } catch (e2) {
      toast.error(e2.response?.data?.message || 'Failed to update leave request');
    } finally {
      setLeaveActionId(null);
    }
  };

  const handleResetPassword = async () => {
    if (!passwordModal.staff) return;
    if (!newPassword || newPassword.trim().length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    setSavingPassword(true);
    try {
      await staffApi.resetPassword(passwordModal.staff.id, { password: newPassword.trim() });
      toast.success('Password updated');
      closeResetPassword();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to update password');
      setSavingPassword(false);
    }
  };

  const tabs = [
    { id: 'list', label: 'Staff List', icon: HiOutlineUserGroup },
    { id: 'attendance', label: 'Attendance', icon: HiOutlineCalendar },
    { id: 'leave-requests', label: 'Leave requests', icon: HiOutlineClipboardList },
    { id: 'payroll', label: 'Payroll', icon: HiOutlineCurrencyRupee },
    { id: 'job-inquiries', label: 'New Inquiry for Job', icon: HiOutlineBriefcase },
  ];

  const clinicNameById = React.useMemo(() => {
    const map = new Map();
    (clinics || []).forEach((c) => map.set(Number(c.id), c.name));
    return map;
  }, [clinics]);

  const resolveClinicAccess = (member) => {
    const raw = member?.clinic_ids ?? member?.clinic_id ?? [];
    let ids = [];
    if (Array.isArray(raw)) {
      ids = raw;
    } else if (typeof raw === 'string') {
      try {
        const parsed = JSON.parse(raw);
        ids = Array.isArray(parsed) ? parsed : [];
      } catch {
        const n = Number(raw);
        ids = Number.isFinite(n) ? [n] : [];
      }
    } else if (raw != null) {
      if (typeof raw === 'object' && raw.type === 'Buffer' && Array.isArray(raw.data)) {
        try {
          const parsed = JSON.parse(String.fromCharCode(...raw.data));
          ids = Array.isArray(parsed) ? parsed : [];
        } catch {
          ids = [];
        }
      } else {
        ids = [raw];
      }
    }
    return ids
      .map((id) => Number(id))
      .filter((id) => Number.isFinite(id) && id > 0)
      .map((id) => ({ id, name: clinicNameById.get(id) || `Clinic #${id}` }));
  };

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-display text-2xl font-bold text-gray-900">Staff Management</h1>
          <p className="text-gray-500 mt-1">Manage staff, attendance, and payroll</p>
        </div>
        {activeTab === 'list' && (
          <button
            onClick={handleAdd}
            className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
          >
            <HiPlus className="w-5 h-5" />
            Add Staff
          </button>
        )}
      </div>

      {/* Tabs */}
      <div className="flex gap-2 bg-gray-100 p-1 rounded-xl w-fit flex-wrap">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
              activeTab === tab.id
                ? 'bg-white text-gray-900 shadow-sm'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            <tab.icon className="w-4 h-4 shrink-0" />
            <span>{tab.label}</span>
            {tab.id === 'leave-requests' && pendingLeaveCount > 0 ? (
              <span className="min-w-[1.25rem] h-5 px-1 flex items-center justify-center rounded-full bg-rose-500 text-white text-[10px] font-bold leading-none">
                {pendingLeaveCount > 99 ? '99+' : pendingLeaveCount}
              </span>
            ) : null}
          </button>
        ))}
      </div>

      {/* Content */}
      {activeTab === 'list' && (
        <div className="card">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="animate-spin rounded-full h-8 w-8 border-2 border-primary-500 border-t-transparent"></div>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {staff.map(member => {
                const avatarSrc = userAvatarSrc(member);
                const clinicAccess = resolveClinicAccess(member);
                return (
                <div key={member.id} className="p-4 border border-gray-200 rounded-xl hover:shadow-md transition-shadow">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 bg-primary-100 rounded-full flex items-center justify-center overflow-hidden shrink-0">
                        {avatarSrc ? (
                          <img
                            src={avatarSrc}
                            alt=""
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <span className="text-primary-700 font-semibold">
                            {member.full_name?.charAt(0)}
                          </span>
                        )}
                      </div>
                      <div>
                        <p className="font-medium">{member.full_name}</p>
                        <p className="text-sm text-gray-500 capitalize">{member.role}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-1">
                      {(user?.role === 'admin' || user?.role === 'clinic_admin') && (
                        <button
                          onClick={() => openResetPassword(member)}
                          className="p-2 text-gray-400 hover:text-amber-600 hover:bg-amber-50 rounded-lg"
                          title="Reset Password"
                        >
                          <HiKey className="w-5 h-5" />
                        </button>
                      )}
                      <button
                        onClick={() => handleEdit(member)}
                        className="p-2 text-gray-400 hover:text-primary-600 hover:bg-primary-50 rounded-lg"
                        title="Edit Staff"
                      >
                        <HiPencil className="w-5 h-5" />
                      </button>
                    </div>
                  </div>
                  <div className="text-sm text-gray-500">
                    <p>{member.email}</p>
                    <p>{member.phone || 'N/A'}</p>
                  </div>
                  <div className="mt-3 flex flex-wrap items-center gap-2">
                    <span className={`badge ${member.is_active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'}`}>
                      {member.is_active ? 'Active' : 'Inactive'}
                    </span>
                    {member.is_in_notice_period && (
                      <span className="badge bg-amber-100 text-amber-800 text-xs">Notice period</span>
                    )}
                    {member.monthly_leave_balance && (
                      <span className="badge bg-blue-100 text-blue-700 text-xs">
                        <LeaveBalanceSummary balance={member.monthly_leave_balance} compact />
                      </span>
                    )}
                    {!member.join_date && (
                      <span className="badge bg-amber-100 text-amber-800 text-xs">Join date required</span>
                    )}
                  </div>
                  <div className="mt-3">
                    <p className="text-xs font-semibold text-gray-500 mb-1">Clinic Access</p>
                    <div className="flex flex-wrap gap-1.5">
                      {clinicAccess.length > 0 ? (
                        clinicAccess.map((clinic) => (
                          <span
                            key={`${member.id}-${clinic.id}`}
                            className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-50 text-blue-700 border border-blue-200"
                          >
                            {clinic.name}
                          </span>
                        ))
                      ) : (
                        <span className="text-xs text-gray-400">No clinic access</span>
                      )}
                    </div>
                  </div>
                </div>
              );
              })}
            </div>
          )}
        </div>
      )}

      {activeTab === 'attendance' && <Attendance />}
      {activeTab === 'payroll' && <Payroll />}
      {activeTab === 'job-inquiries' && <JobInquiries />}

      {activeTab === 'leave-requests' && (
        <div className="card overflow-hidden">
          {leaveLoading ? (
            <div className="flex items-center justify-center h-64">
              <div className="animate-spin rounded-full h-8 w-8 border-2 border-primary-500 border-t-transparent" />
            </div>
          ) : leaveRequests.length === 0 ? (
            <div className="p-8 text-center text-gray-500 text-sm">No leave requests yet.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="text-left text-gray-500 border-b border-gray-200 bg-gray-50/80">
                    <th className="px-4 py-3 font-semibold">Staff</th>
                    <th className="px-4 py-3 font-semibold whitespace-nowrap">Leave dates</th>
                    <th className="px-4 py-3 font-semibold min-w-[12rem]">Reason</th>
                    <th className="px-4 py-3 font-semibold whitespace-nowrap">Requested</th>
                    <th className="px-4 py-3 font-semibold">Status</th>
                    <th className="px-4 py-3 font-semibold text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {leaveRequests.map((row) => {
                    const img = leaveRowAvatar(row);
                    const busy = leaveActionId === row.id;
                    return (
                      <tr key={row.id} className="border-b border-gray-100 hover:bg-gray-50/50">
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-3 min-w-0">
                            <div className="w-10 h-10 rounded-full bg-primary-100 overflow-hidden shrink-0 flex items-center justify-center">
                              {img ? (
                                <img src={img} alt="" className="w-full h-full object-cover" />
                              ) : (
                                <span className="text-primary-700 font-semibold text-sm">
                                  {row.staff_name?.charAt(0) || '?'}
                                </span>
                              )}
                            </div>
                            <div className="min-w-0">
                              <p className="font-medium text-gray-900 truncate">{row.staff_name}</p>
                              <p className="text-xs text-gray-500 truncate">{row.staff_email}</p>
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-gray-800 font-mono text-xs tabular-nums">
                          {formatDateDMY(row.start_date)} → {formatDateDMY(row.end_date)}
                          {row.leave_type ? (
                            <span className="block text-xs text-gray-500 mt-0.5 font-sans">
                              {row.leave_type}
                              {(row.is_half_day === 1 || row.is_half_day === true) && (
                                <span className="ml-1.5 text-[10px] font-semibold px-1.5 py-0.5 rounded bg-purple-100 text-purple-700">
                                  Half day
                                </span>
                              )}
                            </span>
                          ) : null}
                        </td>
                        <td className="px-4 py-3 text-gray-700 max-w-xs">
                          <span className="line-clamp-3">{row.reason}</span>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-gray-600 text-xs font-mono tabular-nums">
                          {formatDateDMY(row.created_at)}
                        </td>
                        <td className="px-4 py-3">
                          <span
                            className={`inline-flex text-xs font-semibold px-2 py-1 rounded-full ${
                              row.status === 'approved'
                                ? 'bg-emerald-100 text-emerald-800'
                                : row.status === 'rejected'
                                  ? 'bg-red-100 text-red-800'
                                  : 'bg-amber-100 text-amber-800'
                            }`}
                          >
                            {row.status}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-right whitespace-nowrap">
                          {row.status === 'pending' ? (
                            <div className="flex flex-wrap justify-end gap-2">
                              <button
                                type="button"
                                disabled={busy}
                                onClick={() => openLeaveEdit(row)}
                                className="px-3 py-1.5 rounded-lg text-xs font-medium bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50"
                              >
                                Edit
                              </button>
                              <button
                                type="button"
                                disabled={busy}
                                onClick={() => handleLeaveStatus(row.id, 'approved')}
                                className="px-3 py-1.5 rounded-lg text-xs font-medium bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50"
                              >
                                Approve
                              </button>
                              <button
                                type="button"
                                disabled={busy}
                                onClick={() => handleLeaveStatus(row.id, 'rejected')}
                                className="px-3 py-1.5 rounded-lg text-xs font-medium bg-white border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                              >
                                Reject
                              </button>
                            </div>
                          ) : row.status === 'approved' && isAdmin ? (
                            <div className="flex flex-wrap justify-end gap-2">
                              <button
                                type="button"
                                disabled={busy}
                                onClick={() => handleLeaveStatus(row.id, 'rejected')}
                                className="px-3 py-1.5 rounded-lg text-xs font-medium bg-white border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                              >
                                Reject
                              </button>
                            </div>
                          ) : (
                            <span className="text-xs text-gray-400">—</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {editingLeave && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-xl mx-4">
            <div className="p-4 border-b border-gray-200 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-900">Edit Leave Request</h3>
              <button
                type="button"
                onClick={closeLeaveEdit}
                className="text-gray-400 hover:text-gray-600"
                aria-label="Close"
              >
                <HiX className="w-5 h-5" />
              </button>
            </div>
            <form onSubmit={handleLeaveEditSubmit} className="p-4 space-y-4">
              <div className="grid sm:grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1">From</label>
                  <input
                    type="date"
                    value={leaveEditForm.start_date}
                    onChange={(e) => {
                      const start = e.target.value;
                      setLeaveEditForm((f) => ({
                        ...f,
                        start_date: start,
                        end_date: f.is_half_day ? start : f.end_date,
                      }));
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    required
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1">To</label>
                  <input
                    type="date"
                    value={isHalfDayLeaveSelected ? leaveEditForm.start_date : leaveEditForm.end_date}
                    onChange={(e) => setLeaveEditForm((f) => ({ ...f, end_date: e.target.value }))}
                    disabled={isHalfDayLeaveSelected}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent disabled:bg-gray-50 disabled:text-gray-500"
                    required
                  />
                </div>
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1">
                  Leave type{monthlyLedgerType ? ' *' : ' (optional)'}
                </label>
                <select
                  value={leaveEditForm.leave_type}
                  onChange={(e) => handleLeaveEditTypeChange(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white"
                  required={Boolean(monthlyLedgerType)}
                >
                  <option value="">—</option>
                  {requestLeaveTypes.map((lt) => (
                    <option key={lt.id} value={lt.name}>
                      {lt.name}
                      {isHalfDayCompanionType(lt) ? ' (0.5 day)' : ''}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1">Reason</label>
                <textarea
                  rows={4}
                  value={leaveEditForm.reason}
                  onChange={(e) => setLeaveEditForm((f) => ({ ...f, reason: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent resize-none"
                  required
                />
              </div>
              <div className="flex justify-end gap-2 pt-1">
                <button
                  type="button"
                  onClick={closeLeaveEdit}
                  className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={leaveActionId === editingLeave.id}
                  className="px-4 py-2 bg-primary-600 text-white rounded-lg text-sm font-medium hover:bg-primary-700 disabled:opacity-60"
                >
                  {leaveActionId === editingLeave.id ? 'Saving...' : 'Save changes'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Staff Form Modal */}
      {showStaffForm && (
        <StaffForm
          staff={selectedStaff}
          onClose={() => {
            setShowStaffForm(false);
            setSelectedStaff(null);
          }}
          onSuccess={handleFormSuccess}
        />
      )}

      {/* Reset Password Modal (Admin only) */}
      {passwordModal.open && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full mx-4">
            <div className="p-5 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">Reset Password</h3>
              <p className="text-sm text-gray-500 mt-1">
                User: <span className="font-medium">{passwordModal.staff?.full_name}</span>
              </p>
            </div>
            <div className="p-5 space-y-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">New Password</label>
                <input
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  placeholder="Minimum 6 characters"
                />
              </div>
              <div className="flex justify-end gap-3 pt-2">
                <button
                  onClick={closeResetPassword}
                  className="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50"
                  disabled={savingPassword}
                >
                  Cancel
                </button>
                <button
                  onClick={handleResetPassword}
                  className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-60"
                  disabled={savingPassword}
                >
                  {savingPassword ? 'Saving...' : 'Update Password'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default Staff;

